import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  NOTIFICATION_LIST_PAGE_SIZE,
} from "@/lib/notifications/build-notification-inbox-query.js";
import { mapNotificationRow } from "@/lib/notifications/map-notification-row.js";
import { resolveNotificationEventName } from "@/lib/notifications/notification-inbox-row.js";
import { notificationEventGroup } from "@/lib/notification-event-presentation.js";

const listInitialState = {
  listItems: [],
  listLoading: false,
  listInitialized: false,
  listSearchParams: {
    page: 1,
    pageSize: NOTIFICATION_LIST_PAGE_SIZE,
    search: "",
    sortBy: "createdAt",
    sortOrder: "desc",
    type: "",
    read: "",
    eventGroup: "",
  },
  listPaginationData: { total: 0, totalPages: 0, page: 1, pageSize: NOTIFICATION_LIST_PAGE_SIZE },
  listUniqueValues: { type: [], read: ["true", "false"] },
};

const initialState = {
  notifications: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  hasMore: false,
  isLoading: false,
  isLoadingMore: false,
  ...listInitialState,
};

function hasAuthToken() {
  try {
    const authState = JSON.parse(localStorage.getItem("auth-storage") || "{}");
    return Boolean(authState?.state?.token);
  } catch {
    return false;
  }
}

function notificationMatchesListFilters(notification, searchParams) {
  if (!notification || !searchParams) return true;
  const { search, type, read, eventGroup } = searchParams;
  if (type && notification.type !== type) return false;
  if (read === "true" && !notification.isRead) return false;
  if (read === "false" && notification.isRead) return false;
  if (eventGroup && notificationEventGroup(resolveNotificationEventName(notification)) !== eventGroup) {
    return false;
  }
  if (search && String(search).trim()) {
    const s = String(search).trim().toLowerCase();
    const title = String(notification.title || "").toLowerCase();
    const message = String(notification.message || "").toLowerCase();
    if (!title.includes(s) && !message.includes(s)) return false;
  }
  return true;
}

function updateReadStateInCollections(state, id, markRead = true) {
  let unreadDelta = 0;
  const mapRow = (n) => {
    if (String(n.id) !== String(id)) return n;
    if (markRead && !n.isRead) unreadDelta = 1;
    return markRead ? { ...n, isRead: true } : n;
  };
  return {
    notifications: state.notifications.map(mapRow),
    listItems: state.listItems.map(mapRow),
    unreadCount: markRead
      ? Math.max(0, state.unreadCount - unreadDelta)
      : state.unreadCount,
  };
}

export const useNotificationStore = create(
  persist(
    (set) => ({
      ...initialState,

      setListSearchParams: (params) =>
        set((state) => ({
          listSearchParams: { ...state.listSearchParams, ...params },
        })),

      applyMarkAsRead: (id) =>
        set((state) => updateReadStateInCollections(state, id, true)),

      applyMarkAllAsRead: () =>
        set((state) => {
          if (state.unreadCount === 0) return state;
          const markAll = (rows) =>
            rows.map((n) => (n.isRead ? n : { ...n, isRead: true }));
          return {
            notifications: markAll(state.notifications),
            listItems: markAll(state.listItems),
            unreadCount: 0,
          };
        }),

      addNotification: (notification) => {
        if (!notification || !notification.id) return;

        const normalized = mapNotificationRow({
          ...notification,
          _id: notification.id,
          body: notification.message,
          read: notification.isRead,
          createdAt: notification.timestamp || notification.createdAt,
        });

        set((state) => {
          const existsBell = state.notifications.some((n) => n.id === normalized.id);
          const existsList = state.listItems.some((n) => n.id === normalized.id);
          if (existsBell && existsList) return state;

          const isUnread = normalized.isRead === false;
          const next = { ...state };

          if (!existsBell) {
            next.notifications = [normalized, ...state.notifications];
            next.unreadCount = state.unreadCount + (isUnread ? 1 : 0);
          }

          const onFirstPage = (state.listSearchParams.page || 1) === 1;
          const matchesFilters = notificationMatchesListFilters(
            normalized,
            state.listSearchParams,
          );
          if (
            state.listInitialized &&
            onFirstPage &&
            matchesFilters &&
            !existsList
          ) {
            next.listItems = [normalized, ...state.listItems];
            const pageSize =
              state.listPaginationData.pageSize || NOTIFICATION_LIST_PAGE_SIZE;
            next.listItems = next.listItems.slice(0, pageSize);
            next.listPaginationData = {
              ...state.listPaginationData,
              total: (state.listPaginationData.total || 0) + 1,
              totalPages: Math.ceil(
                ((state.listPaginationData.total || 0) + 1) / pageSize,
              ),
            };
          }

          return next;
        });
      },

      clearAll: () => {
        set(() => ({
          ...initialState,
        }));
      },

      removeNotification: (id) => {
        set((state) => {
          const notification =
            state.notifications.find((n) => n.id === id) ||
            state.listItems.find((n) => n.id === id);
          const isUnread = notification && !notification.isRead;
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            listItems: state.listItems.filter((n) => n.id !== id),
            unreadCount: Math.max(0, state.unreadCount - (isUnread ? 1 : 0)),
            listPaginationData: {
              ...state.listPaginationData,
              total: Math.max(0, (state.listPaginationData.total || 0) - 1),
            },
          };
        });
      },
    }),
    {
      name: "notification-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
      merge: (persisted, current) => ({
        ...current,
        ...(persisted && typeof persisted === "object" ? persisted : {}),
        page: 1,
        hasMore: false,
        total: 0,
        isLoading: false,
        isLoadingMore: false,
        listItems: [],
        listLoading: false,
        listInitialized: false,
        listSearchParams: listInitialState.listSearchParams,
        listPaginationData: listInitialState.listPaginationData,
        listUniqueValues: listInitialState.listUniqueValues,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (!error && state && hasAuthToken()) {
          void import("@/lib/api/integrations-registry.js").then(
            ({ notificationIntegration }) => {
              notificationIntegration.fetchNotifications(1, false);
            },
          );
        }
      },
    },
  ),
);
